import { c as _c } from "react/compiler-runtime";
import { useEffect, useMemo } from 'react';
import { Combobox as Combo } from '@mantine/core';
import { showError } from 'components/notify';
import { useQuery } from '@tanstack/react-query';
import { useOnDebounce } from 'components/hooks/useOnDebounce';
import { ChildrenProps } from 'shared/ui/uiType';
import { concat } from 'shared/collection';
import { doSafe } from 'shared/promise';
import { escapeRegExp } from 'shared/text';
import { keyBy } from 'shared/collection';
import { MentionProps } from 'shared/ui';
import { values } from 'remeda';
import { match } from 'ts-pattern';
import { MentionNode } from './MentionNode';
import { usePortal } from 'components/portal/portal';
import { SAvatar } from 'components/portal/SAvatar';
import React from 'react';
import { splitNodeContainingQuery, useTriggerMatchedCombo } from './triggerMatch';
import { sb } from 'utils/db';
import { Profile } from 'components/portal/profileTypes';
type MentionItemProps = (MentionProps & {
  type: 'question';
}) | (MentionProps & {
  profile: Pick<Profile, 'id' | 'name' | 'avatar_url'>;
} & {
  type: 'profile';
});
export const useMention = ({
  search,
  context
}: {
  search?: string;
  context: {
    question_id: string;
  };
}) => {
  const {
    profile,
    profilesUI
  } = usePortal();
  const {
    workspace_id
  } = profile ?? {};
  const dbSearch = useOnDebounce(search, {
    debounce: 100
  });
  const enabled = search != null && workspace_id != null;
  const subquestionQ = useQuery({
    queryKey: ['mention.subquestion', dbSearch],
    queryFn: async () => {
      const {
        data,
        error
      } = await sb.from('subquestion').select('id, cfg->>title, question(workspace_id), question_id').ilike('cfg->>title', `%${search}%`).eq('question.workspace_id', workspace_id!).match({
        question_id: context!.question_id
      });
      if (error) showError(error);
      return data;
    },
    enabled
  });
  const searchResults = useMemo(() => profilesUI == null && subquestionQ.data == null || !enabled ? undefined : concat(doSafe(profilesUI, ps => values(ps).filter(p => search != null && p.name?.match(new RegExp(`${escapeRegExp(search)}`, 'i')) != null).map(p_0 => ({
    type: 'profile',
    path: p_0.id,
    title: p_0.name,
    profile: p_0
  }) as MentionItemProps)), subquestionQ.data?.map(q => ({
    type: 'question',
    path: `${q.question_id}/${q.id}`,
    title: q.title
  }) as MentionItemProps)), [subquestionQ.data, profilesUI, enabled]);
  return {
    searchResults
  };
};
export const MentionPlugin = t0 => {
  const $ = _c(18);
  const {
    context
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      trigger: "@"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    targetDivProps,
    store,
    editor,
    triggerMatch,
    reset,
    readonly
  } = useTriggerMatchedCombo(t1);
  const matchingString = triggerMatch?.match.matchingString;
  let t2;
  if ($[1] !== context || $[2] !== matchingString) {
    t2 = {
      search: matchingString,
      context
    };
    $[1] = context;
    $[2] = matchingString;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const {
    searchResults
  } = useMention(t2);
  let t3;
  if ($[4] !== searchResults) {
    t3 = searchResults ? keyBy(searchResults.map(_temp5), _temp6) : undefined;
    $[4] = searchResults;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const selectData = t3;
  let t4;
  if ($[6] !== store) {
    t4 = () => {
      store.selectFirstOption();
    };
    $[6] = store;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== selectData) {
    t5 = [selectData];
    $[8] = selectData;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  useEffect(t4, t5);
  let t6;
  if ($[10] !== editor || $[11] !== readonly || $[12] !== reset || $[13] !== selectData || $[14] !== store || $[15] !== targetDivProps || $[16] !== triggerMatch) {
    t6 = readonly ? <></> : <Combo withinPortal={true} store={store} offset={20} onOptionSubmit={value => {
      reset();
      if (!value) {
        return;
      }
      const mention = selectData?.[value]?.mention;
      if (!mention) {
        return;
      }
      editor.update(() => {
        if (!triggerMatch) {
          return false;
        }
        const node = splitNodeContainingQuery(triggerMatch.match);
        if (!node) {
          return false;
        }
        triggerMatch.selection.insertNodes([new MentionNode(mention)]);
        node.remove();
      });
    }}><Combo.Target><div {...targetDivProps} /></Combo.Target><Combo.Dropdown className="max-h-[90vh] !max-w-[min(100vw,30em)] overflow-y-auto"><Combo.Options>{values(selectData ?? {}).map(_temp7)}</Combo.Options></Combo.Dropdown></Combo>;
    $[10] = editor;
    $[11] = readonly;
    $[12] = reset;
    $[13] = selectData;
    $[14] = store;
    $[15] = targetDivProps;
    $[16] = triggerMatch;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  return t6;
};
function _temp3() {
  return "User";
}
function _temp4() {
  return "Conditional";
}
function _temp5(d) {
  return {
    value: `${d.type}|${d.path}`,
    mention: d,
    label: d.title,
    group: match(d.type).with("profile", _temp3).with("question", _temp4).exhaustive()
  };
}
function _temp6(d_0) {
  return d_0.value;
}
function _temp7(m) {
  return <Combo.Option value={m.value} key={m.value}>{match(m.mention).with({
      type: "profile"
    }, p => <div className="flex items-center gap-2"><SAvatar profile={p.profile} size="sm" />{m.label}</div>).otherwise(() => <>{m.label}</>)}</Combo.Option>;
}